import { type Component, createEffect, createSignal, For, onCleanup, Show } from "solid-js";
import { Portal } from "solid-js/web";
import { Transition } from "@fleet/ui";
import { Bookmark, Check, Copy, FileText, Plus, Printer, Share2, Tag, X } from "@fleet/icons";
import { generateId } from "@fleet/utils";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { useI18n } from "@/providers/i18n-provider";
import { readerStore } from "@/store/reader-store";
import { formatVerseSelectionLabel, serializeVerseSelection } from "@/utils/verse-selection";
import { plainVerseText } from "@/utils/verse-text";
import {
  and,
  bible_bookmark,
  bible_highlight,
  bible_highlight_tag,
  bible_note,
  bible_note_tag,
  bible_note_verse,
  bible_verse_highlight,
  count,
  desc,
  eq,
  inArray,
  isNull,
  type PowerSyncDrizzleDB,
} from "@bso/powersync";
import {
  HIGHLIGHT_PALETTE,
  highlightTextColor,
  normalizeHexColor,
  orderHighlightPalette,
  queryKeys,
  softDeleteBookmarksMutation,
} from "@bso/shared";
import { createPowerSyncQuery } from "@bso/shared/solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { canonicalUrl } from "@/lib/canonical-link";
import type { components } from "@bso/client";

type VerseResponse = components["schemas"]["VerseResponse"];

type ComposeMode = "note" | "highlight";

interface VerseActionsBarProps {
  userId: string | null;
  version: string;
  bookId: number;
  /** Book abbreviation for building shareable reader deep-links. */
  bookAbbr: string;
  chapter: number;
  bookName: string;
  verses: VerseResponse[];
  drizzle: PowerSyncDrizzleDB | null;
  /** Verse numbers (of the current chapter) already bookmarked by the user. */
  bookmarkedVerses: Set<number>;
  /**
   * Highlight color for each verse in the visible chapter. It drives two things
   * in the bar: the order "selection colors -> chapter colors -> remainder" and the
   * gesture **tapping an applied color REMOVES it** (parity with the published app).
   */
  highlightMap: Map<number, string>;
  /**
   * Open editor, governed by the URL (`?compose=`): `note` (note),
   * `highlight` (highlight tags) or `undefined` (closed). This makes the open
   * form reproducible when the URL is pasted/reloaded.
   */
  compose: ComposeMode | undefined;
  /** Opens/closes an editor by writing `?compose=` (the URL is the source of truth). */
  onComposeChange: (mode: ComposeMode | undefined) => void;
  /** Clears the selection and the selection/editor params from the URL. */
  onClearSelection: () => void;
}

const NOTE_COLORS = [
  { name: msg`Yellow`, hex: "#eab308", bgClass: "bg-yellow-500" },
  { name: msg`Green`, hex: "#22c55e", bgClass: "bg-green-500" },
  { name: msg`Blue`, hex: "#3b82f6", bgClass: "bg-blue-500" },
  { name: msg`Pink`, hex: "#ec4899", bgClass: "bg-pink-500" },
  { name: msg`Purple`, hex: "#a855f7", bgClass: "bg-purple-500" },
];

/**
 * Translatable labels for the 10 pastels inherited from the published app. They live
 * here (web layer) because the shared palette is pure DATA and cannot import
 * macros de Lingui.
 */
const PALETTE_LABELS: Record<string, MessageDescriptor> = {
  pink: msg`Pink`,
  peach: msg`Peach`,
  yellow: msg`Yellow`,
  lime: msg`Lime`,
  green: msg`Green`,
  cyan: msg`Cyan`,
  lightBlue: msg`Light blue`,
  periwinkle: msg`Periwinkle`,
  lilac: msg`Lilac`,
  magenta: msg`Magenta`,
};

function paletteLabel(hex: string): MessageDescriptor | null {
  const entry = HIGHLIGHT_PALETTE.find((c) => c.hex === hex);
  return entry ? (PALETTE_LABELS[entry.key] ?? null) : null;
}

export const VerseActionsBar: Component<VerseActionsBarProps> = (props) => {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const [noteText, setNoteText] = createSignal("");
  const [noteTags, setNoteTags] = createSignal("");
  const [noteColor, setNoteColor] = createSignal("#eab308");
  const [highlightTags, setHighlightTags] = createSignal("");

  /**
   * User colors sorted by usage frequency (descending). This is the
   * third palette-order criterion; without it, a free color the
   * user just created would fall outside the 10 visible swatches.
   */
  const colorUsage = createPowerSyncQuery({
    queryKey: () => queryKeys.bible.highlightColorUsage(props.userId ?? undefined),
    db: () => db(),
    queryFn: () => {
      const dbInstance = props.drizzle;
      const u = props.userId;
      if (!dbInstance || !u) {
        return null;
      }
      return dbInstance
        .select({ color: bible_verse_highlight.color, uses: count() })
        .from(bible_verse_highlight)
        .where(and(eq(bible_verse_highlight.user_id, u), isNull(bible_verse_highlight.deleted_at)))
        .groupBy(bible_verse_highlight.color)
        .orderBy(desc(count()));
    },
    enabled: () => Boolean(db()) && Boolean(props.drizzle) && Boolean(props.userId),
  });

  /** Colors applied to the currently selected verses. */
  const selectedColors = () => {
    const out: string[] = [];
    for (const v of readerStore.getSelectedVersesArray()) {
      const c = props.highlightMap.get(v);
      if (c) {
        out.push(c);
      }
    }
    return out;
  };

  /**
   * Swatch order, identical to the published app: selection colors first, then
   * chapter colors, then the most-used colors, and finally the rest of the palette.
   * Limit 10.
   */
  const swatches = () =>
    orderHighlightPalette({
      selectedColors: selectedColors(),
      chapterColors: [...props.highlightMap.values()],
      usedColors: (colorUsage.data ?? []).flatMap((r) => (r.color ? [r.color] : [])),
    });

  /** True when ALL selected verses already have that color. */
  const isColorApplied = (hex: string) => {
    const selected = readerStore.getSelectedVersesArray();
    return (
      selected.length > 0 &&
      selected.every((v) => normalizeHexColor(props.highlightMap.get(v)) === hex)
    );
  };

  /** Cabecera legacy: `John 1: 3-5, 9` (rango compactado). */
  const selectionLabel = () =>
    `${props.bookName} ${props.chapter}: ${formatVerseSelectionLabel(
      readerStore.getSelectedVersesArray(),
    )}`;

  // Open editors derive from the URL (`?compose=`): this makes the form
  // reproducible when the link is pasted/reloaded (scope #15).
  const showNoteEditor = () => props.compose === "note";
  const showHighlightTags = () => props.compose === "highlight";

  // Determine reactively if verses are selected
  const isSelected = () => readerStore.getSelectedVersesArray().length > 0;

  /** Clears the selection and editor state, leaving the URL coherent. */
  function clearAll() {
    setNoteText("");
    setNoteTags("");
    setHighlightTags("");
    props.onClearSelection();
  }

  // Write actions (highlight/bookmark/note) require the local drizzle client
  // and an authenticated user. Until both are ready their handlers no-op, so
  // the buttons must be disabled to avoid silent dead clicks.
  const canWrite = () => Boolean(props.drizzle) && Boolean(props.userId);

  // Escape key listener to clear selection
  createEffect(() => {
    if (isSelected()) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          clearAll();
        }
      };
      globalThis.addEventListener("keydown", handleKeyDown);
      onCleanup(() => {
        globalThis.removeEventListener("keydown", handleKeyDown);
      });
    }
  });

  // Highlight selected verses: writes the flat per-verse rows used by the
  // reader plus a bible_highlight group (origin) so tags and grouped
  // management work like in the legacy app.
  async function handleApplyHighlight(color: string) {
    const dbInstance = props.drizzle;
    const u = props.userId;
    const bId = props.bookId;
    const chNum = props.chapter;
    const currentVersion = props.version;
    if (!dbInstance || !u || Number.isNaN(bId) || Number.isNaN(chNum)) {
      return;
    }

    const selectedList = readerStore.getSelectedVersesArray();
    if (selectedList.length === 0) {
      return;
    }

    try {
      // Remove previous flat highlights on those coordinates and remember
      // which origin groups they belonged to.
      const previousRows = await dbInstance
        .select({
          id: bible_verse_highlight.id,
          origin_id: bible_verse_highlight.origin_id,
        })
        .from(bible_verse_highlight)
        .where(
          and(
            eq(bible_verse_highlight.user_id, u),
            eq(bible_verse_highlight.version, currentVersion),
            eq(bible_verse_highlight.book, bId),
            eq(bible_verse_highlight.chapter, chNum),
            inArray(bible_verse_highlight.verse, selectedList),
          ),
        );

      if (previousRows.length > 0) {
        await dbInstance.delete(bible_verse_highlight).where(
          inArray(
            bible_verse_highlight.id,
            previousRows.map((r) => r.id),
          ),
        );
      }

      // Garbage-collect origin groups left without any verse
      const affectedOrigins = [
        ...new Set(previousRows.flatMap((r) => (r.origin_id ? [r.origin_id] : []))),
      ];
      if (affectedOrigins.length > 0) {
        const stillReferenced = await dbInstance
          .select({ origin_id: bible_verse_highlight.origin_id })
          .from(bible_verse_highlight)
          .where(inArray(bible_verse_highlight.origin_id, affectedOrigins));
        const aliveOrigins = new Set(stillReferenced.map((r) => r.origin_id));
        const orphanOrigins = affectedOrigins.filter((o) => !aliveOrigins.has(o));
        if (orphanOrigins.length > 0) {
          await dbInstance
            .delete(bible_highlight_tag)
            .where(inArray(bible_highlight_tag.highlight_id, orphanOrigins));
          await dbInstance
            .delete(bible_highlight)
            .where(inArray(bible_highlight.id, orphanOrigins));
        }
      }

      if (color) {
        const now = new Date().toISOString();
        const groupId = generateId();

        // The group must be inserted before its tags so the server-side FK
        // (bible_highlight_tag → bible_highlight) is satisfied during sync.
        await dbInstance.insert(bible_highlight).values({
          id: groupId,
          user_id: u,
          color,
          created_at: now,
          updated_at: now,
        });

        const tagsList = highlightTags()
          .split(",")
          .map((s) => s.trim())
          .filter((s) => s.length > 0);
        for (const tag of tagsList) {
          // Sequential inserts keep CRUD queue order deterministic
          // oxlint-disable-next-line no-await-in-loop
          await dbInstance.insert(bible_highlight_tag).values({
            id: generateId(),
            highlight_id: groupId,
            tag,
          });
        }

        await Promise.all(
          selectedList.map((verseNum) =>
            dbInstance.insert(bible_verse_highlight).values({
              id: generateId(),
              user_id: u,
              version: currentVersion,
              book: bId,
              chapter: chNum,
              verse: verseNum,
              color,
              origin_id: groupId,
              created_at: now,
              updated_at: now,
            }),
          ),
        );
      }

      setHighlightTags("");
      clearAll();
    } catch (e) {
      console.error("Failed to apply highlight:", e);
    }
  }

  // Print selected verses (legacy parity: print-verse). Renders the selection
  // into a hidden iframe and triggers the print dialog.
  function handlePrintSelected() {
    const fullText = buildSelectionText();
    if (!fullText) {
      return;
    }

    const [reference, ...lines] = fullText.split("\n");
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.append(iframe);

    const doc = iframe.contentDocument;
    if (!doc) {
      iframe.remove();
      return;
    }

    const title = doc.createElement("h1");
    title.textContent = reference ?? "";
    doc.body.append(title);
    for (const line of lines) {
      const p = doc.createElement("p");
      p.textContent = line;
      doc.body.append(p);
    }
    const style = doc.createElement("style");
    style.textContent =
      "body{font-family:Georgia,serif;padding:2rem;color:#111}h1{font-size:1.1rem;margin-bottom:1rem}p{font-size:1rem;line-height:1.7;margin:0.35rem 0}";
    doc.head.append(style);

    const PRINT_CLEANUP_DELAY_MS = 1000;
    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();
    // Give the print dialog time to capture the document before cleanup
    setTimeout(() => {
      iframe.remove();
    }, PRINT_CLEANUP_DELAY_MS);
    clearAll();
  }

  // Copy selected verses to clipboard. Attaches the shareable deep link (the URL
  // with `?sel=`) in addition to the text, so the recipient can open the
  // exact selection in the reader.
  function handleCopySelected() {
    const fullText = buildSelectionText();
    if (!fullText) {
      return;
    }

    const link = buildShareUrl();
    void navigator.clipboard.writeText(link ? `${fullText}\n\n${link}` : fullText);
    clearAll();
  }

  function buildSelectionText(): string | null {
    const selectedList = readerStore.getSelectedVersesArray();
    if (selectedList.length === 0) {
      return null;
    }

    const body = selectedList
      .map((verseNum) => {
        const v = props.verses.find((item) => item.verse === verseNum);
        // Single source of truth for format knowledge: the tokenizer.
        const cleanText = v ? plainVerseText(v.text) : "";
        return `[${verseNum}] ${cleanText}`;
      })
      .join("\n");

    const reference = `${props.bookName} ${props.chapter}:${selectedList.join(", ")} (${props.version.toUpperCase()})`;
    return `${reference}\n${body}`;
  }

  /**
   * Absolute reader URL with the active selection (`?sel=`) — a deep link that
   * reproduces exactly the selected passage in another browser. Returns
   * `null` if there is no selection or no `window` (SSR/tests without a complete DOM).
   */
  function buildShareUrl(): string | null {
    const selectedList = readerStore.getSelectedVersesArray();
    if (selectedList.length === 0 || globalThis.location === undefined) {
      return null;
    }
    const sel = serializeVerseSelection(selectedList);
    const path = `/bible/${encodeURIComponent(props.version)}/${encodeURIComponent(
      props.bookAbbr,
    )}/${props.chapter}`;
    const url = new URL(path, globalThis.location.origin);
    url.searchParams.set("sel", sel);
    return canonicalUrl(url.toString());
  }

  // Share selected verses (legacy parity: sharing module). Uses the native
  // share sheet when available and falls back to the clipboard. Incluye el
  // shareable deep link (`?sel=`) so the recipient opens exactly the
  // same selection in the reader (audit gap #8).
  async function handleShareSelected() {
    const fullText = buildSelectionText();
    if (!fullText) {
      return;
    }

    const shareUrl = buildShareUrl();

    try {
      if (typeof navigator.share === "function") {
        await navigator.share({
          text: fullText,
          ...(shareUrl ? { url: shareUrl } : {}),
        });
      } else {
        await navigator.clipboard.writeText(shareUrl ? `${fullText}\n\n${shareUrl}` : fullText);
      }
      clearAll();
    } catch (e) {
      // AbortError means the user dismissed the share sheet — not a failure
      if (!(e instanceof DOMException && e.name === "AbortError")) {
        console.error("Failed to share verses:", e);
      }
    }
  }

  // Create a note and tags for selected verses
  async function handleSaveNote() {
    const dbInstance = props.drizzle;
    const u = props.userId;
    const bId = props.bookId;
    const chNum = props.chapter;
    const currentVersion = props.version;
    const selectedList = readerStore.getSelectedVersesArray();
    if (!dbInstance || !u || Number.isNaN(bId) || Number.isNaN(chNum)) {
      return;
    }

    if (selectedList.length === 0 || !noteText().trim()) {
      return;
    }

    try {
      const noteId = generateId();
      const now = new Date().toISOString();

      // 1. Insert Note
      await dbInstance.insert(bible_note).values({
        id: noteId,
        user_id: u,
        text_content: noteText().trim(),
        color: noteColor(),
        created_at: now,
        updated_at: now,
      });

      // 2. Insert Linked Verses
      const versePromises = selectedList.map((verseNum) =>
        dbInstance.insert(bible_note_verse).values({
          id: generateId(),
          note_id: noteId,
          version: currentVersion,
          book: bId,
          chapter: chNum,
          verse: verseNum,
        }),
      );
      await Promise.all(versePromises);

      // 3. Insert tags if present
      const tagsInput = noteTags().trim();
      if (tagsInput) {
        const tagsList = tagsInput
          .split(",")
          .map((s) => s.trim())
          .filter((s) => s.length > 0);

        const tagPromises = tagsList.map((tag) =>
          dbInstance.insert(bible_note_tag).values({
            id: generateId(),
            note_id: noteId,
            tag,
          }),
        );
        await Promise.all(tagPromises);
      }

      // Reset editor and clear selection
      clearAll();
    } catch (e) {
      console.error("Failed to save note for verses:", e);
    }
  }

  // True when every selected verse is already bookmarked → the button removes
  // them; otherwise it adds the missing ones (favorite toggle).
  const allSelectedBookmarked = () => {
    const selected = readerStore.getSelectedVersesArray();
    return selected.length > 0 && selected.every((v) => props.bookmarkedVerses.has(v));
  };

  // Toggle bookmarks/favorites for the selected verses. Personal user data:
  // syncs via PowerSync by user_id. CC-7: awaited so it is not lost on unmount.
  async function handleToggleBookmark() {
    const dbInstance = props.drizzle;
    const u = props.userId;
    const bId = props.bookId;
    const chNum = props.chapter;
    const currentVersion = props.version;
    if (!dbInstance || !u || Number.isNaN(bId) || Number.isNaN(chNum)) {
      return;
    }

    const selectedList = readerStore.getSelectedVersesArray();
    if (selectedList.length === 0) {
      return;
    }

    try {
      const now = new Date().toISOString();

      if (allSelectedBookmarked()) {
        // Logical delete of the active bookmarks on the selected coordinates.
        // Resolve to ids FIRST, then patch by id (verified) — a coordinate
        // WHERE on the UPDATE itself would reopen the ghost-update window: if
        // a bookmark was just inserted moments ago (the toggle's own "add"
        // branch, seconds earlier in the same reading session), the UPDATE
        // could match it by coordinates OR miss it entirely depending on
        // whether the view has caught up — there would be no id to retry
        // against. `softDeleteBookmarksMutation` needs concrete ids to reread.
        const toRemove = await dbInstance
          .select({ id: bible_bookmark.id })
          .from(bible_bookmark)
          .where(
            and(
              eq(bible_bookmark.user_id, u),
              eq(bible_bookmark.version, currentVersion),
              eq(bible_bookmark.book, bId),
              eq(bible_bookmark.chapter, chNum),
              inArray(bible_bookmark.verse, selectedList),
              isNull(bible_bookmark.deleted_at),
            ),
          );
        await softDeleteBookmarksMutation(
          dbInstance,
          toRemove.map((r) => r.id),
        );
      } else {
        // Add a bookmark only for the verses not already bookmarked.
        const toAdd = selectedList.filter((v) => !props.bookmarkedVerses.has(v));
        await Promise.all(
          toAdd.map((verseNum) =>
            dbInstance.insert(bible_bookmark).values({
              id: generateId(),
              user_id: u,
              version: currentVersion,
              book: bId,
              chapter: chNum,
              verse: verseNum,
              created_at: now,
              updated_at: now,
            }),
          ),
        );
      }

      clearAll();
    } catch (e) {
      console.error("Failed to toggle bookmark:", e);
    }
  }

  return (
    <Portal>
      {/* Bottom Sheet Panel Transition */}
      <Transition name="bottom-sheet">
        <Show when={isSelected()}>
          <div
            class="fixed bottom-0 left-0 right-0 max-w-xl mx-auto bg-zinc-900 text-zinc-100 border-t border-zinc-800/80 rounded-t-[28px] shadow-2xl z-40 flex flex-col overflow-hidden pb-[calc(1rem+env(safe-area-inset-bottom,0px))]"
            style={{ "will-change": "transform" }}
          >
            {/* Header / Info bar — reference with the COMPACTED range
              (`John 1: 3-5, 9`), como la hoja de la app publicada. */}
            <div class="flex items-center justify-between px-6 pt-4 pb-2 gap-3">
              <span
                class="text-xs font-bold text-zinc-300 truncate"
                data-testid="verse-selection-label"
              >
                {selectionLabel()}
              </span>
              <button
                type="button"
                onClick={clearAll}
                class="flex items-center gap-1 text-[11px] font-bold text-zinc-500 hover:text-zinc-300 transition-colors"
              >
                <X class="w-3 h-3" />
                <span>{t(msg`Clear`)}</span>
              </button>
            </div>

            {/* Actions Bar */}
            <div class="flex items-center justify-between px-6 py-2 gap-4">
              {/* Paleta de resaltado: `+` (color libre) + swatches ordenados
                "usados primero". Tocar un color YA aplicado lo QUITA. */}
              <div class="flex items-center gap-2 overflow-x-auto scrollbar-hide min-w-0">
                {/* `+` → free color. The legacy three-slider HSV picker is
                  sustituye por el selector nativo: el valor estaba en poder
                  elegir cualquier color, no en esos sliders. */}
                <label
                  class="relative w-7 h-7 shrink-0 rounded-full bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border border-zinc-600/70 flex items-center justify-center transition-all shadow-sm cursor-pointer"
                  classList={{ "opacity-40 cursor-not-allowed": !canWrite() }}
                  title={t(msg`Pick a custom highlight color`)}
                  data-testid="highlight-custom-color"
                >
                  <Plus class="w-3.5 h-3.5" />
                  {/* `w-full h-full` is NOT redundant with `inset-0`: a
                    `<input type="color">` es un elemento REEMPLAZADO y con
                    `width:auto` conserva su ancho intrínseco (~50 px en
                    Chrome), así que se desbordaba del chip de 28 px y su capa
                    invisible se comía los clicks del PRIMER swatch de color.
                    Lo detectó el e2e de la hoja de resaltado. */}
                  <input
                    type="color"
                    disabled={!canWrite()}
                    class="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                    aria-label={t(msg`Pick a custom highlight color`)}
                    onChange={async (e) => {
                      const hex = normalizeHexColor(e.currentTarget.value);
                      if (hex) {
                        await handleApplyHighlight(hex);
                      }
                    }}
                  />
                </label>
                <For each={swatches()}>
                  {(hex) => {
                    const label = paletteLabel(hex);
                    const colorName = label ? t(label) : hex;
                    const applied = () => isColorApplied(hex);
                    return (
                      <button
                        type="button"
                        disabled={!canWrite()}
                        data-testid={`highlight-swatch-${hex.slice(1)}`}
                        data-applied={applied() ? "true" : undefined}
                        aria-pressed={applied()}
                        onClick={async () => {
                          // Paridad legacy: el swatch es un TOGGLE.
                          await handleApplyHighlight(applied() ? "" : hex);
                        }}
                        class="w-7 h-7 shrink-0 rounded-full border hover:scale-115 active:scale-95 transition-all shadow-sm disabled:opacity-40 disabled:hover:scale-100 disabled:cursor-not-allowed flex items-center justify-center"
                        classList={{
                          "border-white ring-2 ring-white/70": applied(),
                          "border-black/20": !applied(),
                        }}
                        style={{ "background-color": hex }}
                        title={
                          applied()
                            ? t(msg`Remove ${colorName} highlight`)
                            : t(msg`Highlight in ${colorName}`)
                        }
                      >
                        <Show when={applied()}>
                          <Check class="w-3.5 h-3.5" style={{ color: highlightTextColor(hex) }} />
                        </Show>
                      </button>
                    );
                  }}
                </For>
                <button
                  type="button"
                  disabled={!canWrite()}
                  onClick={async () => {
                    await handleApplyHighlight("");
                  }}
                  class="w-7 h-7 shrink-0 rounded-full bg-zinc-800 hover:bg-zinc-700 active:scale-95 text-zinc-400 hover:text-zinc-200 border border-zinc-700/60 flex items-center justify-center transition-all shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
                  title={t(msg`Remove Highlight`)}
                  data-testid="highlight-remove"
                >
                  <X class="w-3.5 h-3.5" />
                </button>
              </div>

              <div class="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    props.onComposeChange(showHighlightTags() ? undefined : "highlight");
                  }}
                  class="w-7 h-7 rounded-full flex items-center justify-center transition-all shadow-sm border active:scale-95"
                  classList={{
                    "bg-accent hover:bg-accent/90 text-accent-foreground border-transparent":
                      showHighlightTags(),
                    "bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200 border-zinc-700/60":
                      !showHighlightTags(),
                  }}
                  title={t(msg`Highlight tags`)}
                  aria-pressed={showHighlightTags()}
                >
                  <Tag class="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Extra tools */}
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  data-testid="bookmark-toggle"
                  disabled={!canWrite()}
                  onClick={async () => {
                    await handleToggleBookmark();
                  }}
                  class="flex items-center justify-center w-9 h-9 rounded-xl transition-all shadow-sm border disabled:opacity-40 disabled:cursor-not-allowed"
                  classList={{
                    "bg-amber-500 hover:bg-amber-400 text-white border-transparent":
                      allSelectedBookmarked(),
                    "bg-zinc-800 hover:bg-zinc-750 text-zinc-300 hover:text-zinc-100 border-zinc-700/50":
                      !allSelectedBookmarked(),
                  }}
                  title={allSelectedBookmarked() ? t(msg`Remove bookmark`) : t(msg`Bookmark`)}
                  aria-pressed={allSelectedBookmarked()}
                >
                  <Bookmark
                    class="w-4.5 h-4.5"
                    classList={{ "fill-current": allSelectedBookmarked() }}
                  />
                </button>

                <button
                  type="button"
                  onClick={() => {
                    props.onComposeChange(showNoteEditor() ? undefined : "note");
                  }}
                  class="flex items-center justify-center w-9 h-9 rounded-xl transition-all shadow-sm border"
                  classList={{
                    "bg-accent hover:bg-accent/90 text-accent-foreground border-transparent":
                      showNoteEditor(),
                    "bg-zinc-800 hover:bg-zinc-750 text-zinc-300 hover:text-zinc-100 border-zinc-700/50":
                      !showNoteEditor(),
                  }}
                  title={t(msg`Add Note`)}
                >
                  <FileText class="w-4.5 h-4.5" />
                </button>

                <button
                  type="button"
                  onClick={handleCopySelected}
                  class="flex items-center gap-1.5 px-4 h-9 rounded-xl bg-zinc-800 hover:bg-zinc-750 text-zinc-300 hover:text-zinc-100 border border-zinc-700/50 text-xs font-bold transition-all shadow-sm active:scale-95"
                >
                  <Copy class="w-3.5 h-3.5" />
                  <span>{t(msg`Copy`)}</span>
                </button>

                <button
                  type="button"
                  onClick={async () => {
                    await handleShareSelected();
                  }}
                  class="flex items-center gap-1.5 px-4 h-9 rounded-xl bg-zinc-800 hover:bg-zinc-750 text-zinc-300 hover:text-zinc-100 border border-zinc-700/50 text-xs font-bold transition-all shadow-sm active:scale-95"
                  title={t(msg`Share selected verses`)}
                >
                  <Share2 class="w-3.5 h-3.5" />
                  <span>{t(msg`Share`)}</span>
                </button>

                <button
                  type="button"
                  onClick={handlePrintSelected}
                  class="flex items-center justify-center w-9 h-9 rounded-xl bg-zinc-800 hover:bg-zinc-750 text-zinc-300 hover:text-zinc-100 border border-zinc-700/50 transition-all shadow-sm active:scale-95"
                  title={t(msg`Print selected verses`)}
                >
                  <Printer class="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Highlight tags input (applied on the next color click) */}
            <Show when={showHighlightTags()}>
              <div class="px-6 pt-2 animate-in fade-in slide-in-from-bottom-2 duration-200">
                <div class="relative">
                  <Tag class="w-3.5 h-3.5 text-zinc-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={highlightTags()}
                    onInput={(e) => setHighlightTags(e.currentTarget.value)}
                    placeholder={t(msg`Highlight tags (comma-separated), then choose a color`)}
                    class="w-full bg-zinc-950 text-zinc-100 placeholder-zinc-600 rounded-xl pl-9 pr-4 py-2 text-2xs border border-zinc-800 focus:outline-none focus:border-zinc-700 transition-colors"
                  />
                </div>
              </div>
            </Show>

            {/* Note Editor Area (Collapsible) */}
            <Show when={showNoteEditor()}>
              <div class="mt-2 border-t border-zinc-800/60 px-6 pt-4 space-y-3 animate-in fade-in slide-in-from-bottom-2 duration-200">
                <div class="flex items-center justify-between">
                  <span class="text-xs font-bold text-zinc-300">{t(msg`Note for verses:`)}</span>
                  {/* Note color picker */}
                  <div class="flex items-center gap-1.5">
                    <For each={NOTE_COLORS}>
                      {(color) => (
                        <button
                          type="button"
                          onClick={() => setNoteColor(color.hex)}
                          class="w-4 h-4 rounded-full relative transition-all border border-black/20"
                          style={{ "background-color": color.hex }}
                        >
                          <Show when={noteColor() === color.hex}>
                            <Check class="w-2.5 h-2.5 text-zinc-900 absolute inset-0 m-auto" />
                          </Show>
                        </button>
                      )}
                    </For>
                  </div>
                </div>

                <textarea
                  value={noteText()}
                  onInput={(e) => setNoteText(e.currentTarget.value)}
                  placeholder={t(msg`Write your personal note here...`)}
                  rows="3"
                  class="w-full bg-zinc-950 text-zinc-100 placeholder-zinc-500 rounded-xl px-4 py-3 text-xs border border-zinc-800 focus:outline-none focus:border-zinc-700 transition-colors resize-none scrollbar-hide"
                />

                <div class="flex items-center gap-2">
                  <div class="relative flex-1">
                    <Tag class="w-3.5 h-3.5 text-zinc-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={noteTags()}
                      onInput={(e) => setNoteTags(e.currentTarget.value)}
                      placeholder={t(msg`Tags (comma-separated)`)}
                      class="w-full bg-zinc-950 text-zinc-100 placeholder-zinc-600 rounded-xl pl-9 pr-4 py-2 text-2xs border border-zinc-800 focus:outline-none focus:border-zinc-700 transition-colors"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={async () => {
                      await handleSaveNote();
                    }}
                    disabled={!noteText().trim() || !canWrite()}
                    class="h-8 px-4 rounded-xl bg-accent hover:bg-accent/90 disabled:opacity-40 disabled:hover:bg-accent text-accent-foreground text-xs font-bold transition-all shadow-sm shrink-0 active:scale-95"
                  >
                    {t(msg`Save`)}
                  </button>
                </div>
              </div>
            </Show>
          </div>
        </Show>
      </Transition>
    </Portal>
  );
};
